import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from '@g4/moment-utc';
import { hook, withValue } from '@g4/automation-hooks';

import { useTranslations } from '../../../../../graphql/components/TranslationsProvider';

import OvernightBadge from '../../../../OvernightBadge';
import { Box } from '../../../../Boneyard';
import { UNITED_STATES } from '../../../../../utils/constants/countriesCodeUtil';

const propTypes = {
  leg: PropTypes.string.isRequired,
  dateTime: PropTypes.string.isRequired,
  origin: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
  }),
  destination: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
  }),
  isOvernight: PropTypes.bool,
  automationHook: PropTypes.string,
  isTripSummary: PropTypes.bool,
};

export const legTypes = {
  DEPARTING: 'departing',
  RETURNING: 'returning',
};

const DateTime = styled.span`
  font-size: ${props => props.theme.toRem(14)};
  font-weight: 500;
  margin-right: ${props => props.theme.spacing(2)};

  ${props => props.theme.query.md} {
    font-size: ${props => props.theme.toRem(16)};
  }
`;

const Time = styled.span`
  font-size: ${props => props.theme.toRem(12)};
  text-transform: uppercase;
  font-weight: normal;
  padding-left: 0.375rem;
  padding-right: 0.125rem;

  ${props => props.theme.query.md} {
    font-size: ${props =>
      props.isTripSummary ? props.theme.toRem(16) : props.theme.toRem(14)};
  }
`;

const Route = styled.div`
  font-size: ${props => props.theme.toRem(12)};
  font-weight: 500;

  ${props => props.theme.query.md} {
    font-size: ${props =>
      props.isTripSummary ? props.theme.toRem(16) : props.theme.toRem(14)};
  }
`;

const BoldTime = styled.time`
  font-weight: 600;
  ${props => props.theme.query.md} {
    font-size: ${props =>
      props.isTripSummary ? props.theme.toRem(20) : props.theme.toRem(16)};
  }
`;

const BoldSpan = styled.span`
  font-weight: 600;
  ${props => props.theme.query.md} {
    font-size: ${props =>
      props.isTripSummary ? props.theme.toRem(20) : props.theme.toRem(16)};
  }
`;

const Flight = ({
  leg,
  dateTime,
  origin,
  destination,
  isOvernight,
  automationHook,
  isTripSummary,
}) => {
  const translations = useTranslations();
  const { travelToSymbol, overnightBadgeLabel } = isTripSummary
    ? translations.paymentPage
    : translations.cart;
  const { departingLegLabel, returningLegLabel } = translations.common;

  const momentDateTime = moment(dateTime);
  const formatAirport = airport =>
    `${airport.city}${', '}
  ${
    airport.country === UNITED_STATES
      ? airport.state
      : airport.country.toUpperCase()
  } ${' '}
   (${airport.code})`;

  const isDeparting = leg === legTypes.DEPARTING;
  const legText = isDeparting ? departingLegLabel : returningLegLabel;

  const dateFormat = 'ddd, MMM DD';
  const timeFormat = 'h:mm A';

  return (
    <Box ml={2} {...hook(automationHook)} w="100%">
      <Box d="flex" mb={1}>
        <DateTime>
          <BoldSpan
            {...hook(automationHook, 'leg')}
            isTripSummary={isTripSummary}
          >
            {legText + ': '}
          </BoldSpan>
          <BoldTime
            dateTime={dateTime}
            {...hook(automationHook, 'date')}
            isTripSummary={isTripSummary}
          >
            {momentDateTime.format(dateFormat)}
            {isTripSummary ? ', ' : ' '}
            {!isTripSummary && (
              <Time
                {...hook(automationHook, 'time')}
                isTripSummary={isTripSummary}
              >
                {momentDateTime.format(timeFormat)}
              </Time>
            )}
          </BoldTime>
          {isTripSummary && (
            <BoldTime
              {...hook(automationHook, 'time')}
              isTripSummary={isTripSummary}
            >
              {momentDateTime.format(timeFormat)}
            </BoldTime>
          )}
        </DateTime>

        {isOvernight && (
          <OvernightBadge
            label={overnightBadgeLabel}
            automationHook={withValue(automationHook, 'overnight')}
          />
        )}
      </Box>

      <Route isTripSummary={isTripSummary}>
        <span {...hook(automationHook, 'origin')}>{formatAirport(origin)}</span>
        <span>{' ' + travelToSymbol + ' '}</span>
        <span {...hook(automationHook, 'destination')}>
          {formatAirport(destination)}
        </span>
      </Route>
    </Box>
  );
};

Flight.propTypes = propTypes;

export default Flight;
